/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { isRightToLeft } from 'js/lib/language';

import { Link, Typography, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { SectionName, UpsellType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { numberOfProducts } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import UpsellCard from 'bundles/unified-description-page-common/components/modules/upsell/UpsellCard';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useXdpSubscriptionsForwardDiscovery } from 'bundles/unified-description-page-common/providers/SubscriptionsForwardDiscoveryDataProvider';
import {
  ALTERNATE_UPSELL_CARD_IMAGE_HEIGHT,
  DEFAULT_UPSELL_CARD_IMAGE_HEIGHT,
} from 'bundles/unified-description-page-common/utils/useSubscriptionFirstExpUtils';

import courseraPlusLogoInvert from 'images/unified-common/coursera-plus-logo-invert.svg';
import subscriptionFirstDesktopImage from 'images/unified-common/cplus-upsell-med-card-mdup.png';
import subscriptionFirstMobileImage from 'images/unified-common/cplus-upsell-small-banner-xs.png';
import courseraPlusLogo from 'images/unified-description-page/courseraplus-blue.png';
import standaloneUpsellModuleCourseraPlus from 'images/unified-description-page/individual-upsell-coursera-plus.png';
import upsellModuleCourseraPlus from 'images/unified-description-page/upsell-module-coursera-plus.png';

import _t from 'i18n!nls/xdp';

// This prevents the card text from overlapping with the image in desktop view.
const CARD_HEIGHT_ADJUSTMENT = 30;

type Props = {
  // This will be added dynamically from UpsellModule.
  amountOfCards?: number;
};

const styles = {
  cta: css`
    color: var(--cds-color-interactive-primary);
  `,
  subscriptionFirstInvertColor: css`
    color: var(--cds-color-neutral-primary-invert);
  `,
  wrapperContainerStyle: css`
    background-color: var(--cds-color-callouts-background-primary);
  `,
  imgContainerStyle: (isRTL: boolean) => css`
    ${breakpoints.up('md')} {
      img {
        object-position: bottom right !important;
        object-fit: contain !important;
        ${isRTL &&
        `
          object-position: bottom left !important;
          transform: scale(-1, 1);
        `}
      }
    }
  `,
};

const CourseraPlusUpsellCard = ({ amountOfCards }: Props) => {
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const { shouldShowXdpChanges: isSubscriptionFirst } = useXdpSubscriptionsForwardDiscovery();
  const isDesktop = useMediaQuery(breakpoints.up('lg'));
  const isStandaloneCard = isDesktop && amountOfCards === 1;
  const isDoubleCard = !isMobile && amountOfCards === 2;
  const imageHeight =
    isSubscriptionFirst && isDoubleCard ? ALTERNATE_UPSELL_CARD_IMAGE_HEIGHT - CARD_HEIGHT_ADJUSTMENT : undefined;
  const containerHeight = isSubscriptionFirst && isDoubleCard ? DEFAULT_UPSELL_CARD_IMAGE_HEIGHT : undefined;
  const isRTL = isRightToLeft(_t.getLocale());

  const track = useTracker();
  const eventingProductData = useEventingProductData();
  const trackingData = {
    upsellType: 'coursera_plus' as UpsellType,
    pageSection: {
      sectionName: 'upsell' as SectionName,
    },
    ...eventingProductData,
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_upsell_card',
    trackingData
  );

  let backgroundImageSrc = upsellModuleCourseraPlus;
  if (isMobile && isSubscriptionFirst) {
    backgroundImageSrc = subscriptionFirstMobileImage;
  } else if (isSubscriptionFirst) {
    backgroundImageSrc = subscriptionFirstDesktopImage;
  } else if (isStandaloneCard) {
    backgroundImageSrc = standaloneUpsellModuleCourseraPlus;
  }

  const getBackgroundColor = () => {
    if (isSubscriptionFirst) {
      return 'var(--cds-color-callouts-background-primary)';
    }
    return undefined;
  };

  return (
    <UpsellCard
      {...(isSubscriptionFirst && {
        backgroundImageContainerStyle: styles.imgContainerStyle(isRTL),
        wrapperContainerStyle: styles.wrapperContainerStyle,
        imageHeight,
        containerHeight,
      })}
      logo={isSubscriptionFirst ? courseraPlusLogoInvert : courseraPlusLogo}
      title={
        <Typography variant="h1semibold" component="h2" color={isSubscriptionFirst ? 'invertBody' : 'body'}>
          {isSubscriptionFirst
            ? _t('Unlock access to #{numberOfProducts} courses with a subscription', { numberOfProducts })
            : _t('Open new doors with Coursera Plus')}
        </Typography>
      }
      description={
        <Typography2 variant="bodySecondary" component="p" color={isSubscriptionFirst ? 'invertBody' : 'body'}>
          {isSubscriptionFirst
            ? _t(
                'Explore roles and skills, learn more effectively with Coursera Coach, and earn recognized credentials'
              )
            : _t(
                'Unlimited access to #{numberOfProducts} world-class courses, hands-on projects, and job-ready certificate programs - all included in your subscription',
                { numberOfProducts }
              )}
        </Typography2>
      }
      cta={
        <Link
          component={TrackedA}
          href="/courseraplus"
          trackingName="upsell_cta_courseraplus"
          typographyVariant="subtitleMedium"
          variant="quiet"
          css={isSubscriptionFirst ? styles.subscriptionFirstInvertColor : styles.cta}
          {...(isSubscriptionFirst && { invert: true })}
          aria-label={_t('Learn more about Coursera Plus')}
          icon={<ArrowNextIcon />}
          onClick={() => {
            track('click_upsell_card', trackingData);
          }}
        >
          {_t('Learn more')}
        </Link>
      }
      backgroundImage={backgroundImageSrc}
      backgroundPosition="90%"
      backgroundColor={getBackgroundColor()}
      amountOfCards={amountOfCards}
      ref={visibilityTrackingRef}
    />
  );
};

export default CourseraPlusUpsellCard;
